import { useState, type CSSProperties, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import vocaLogoUrl from '@/assets/images/vocalogo.svg'
import vocaPreviewUrl from '@/assets/images/vocapreview.webp'
import sprintoLogoUrl from '@/assets/images/sprintologo.svg'
import sprintoPreviewUrl from '@/assets/images/sprintopreview.webp'
import dispatchlyLogoUrl from '@/assets/images/dispatchlylogo.svg'
import dispatchlyPreviewUrl from '@/assets/images/dispatchlypreview.webp'

interface Product {
  name: string
  description: string
  href: string
  icon: ReactNode
  badge?: string
}

interface Suite {
  label: string
  color: string
  products: Product[]
}

const suites: Suite[] = [
  {
    label: 'Business Suite',
    color: 'text-brand-red',
    products: [
      {
        name: 'Sales 360',
        description: 'AI-powered sales pipeline and lead orchestration',
        href: '#',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M15 16.6666V8.33325M10 16.6666V3.33325M5 16.6666V11.6666"
              stroke="#2D2D2D"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        name: 'Support 360',
        description: 'Unified customer support and ticketing',
        href: '#',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M15 15.8334H15.8333C16.2754 15.8334 16.6993 15.6578 17.0118 15.3453C17.3244 15.0327 17.5 14.6088 17.5 14.1667V9.16675C17.5 7.17762 16.7098 5.26997 15.3033 3.86345C13.8968 2.45692 11.9891 1.66675 10 1.66675C8.01088 1.66675 6.10322 2.45692 4.6967 3.86345C3.29018 5.26997 2.5 7.17762 2.5 9.16675V14.1667C2.5 14.6088 2.67559 15.0327 2.98816 15.3453C3.30072 15.6578 3.72464 15.8334 4.16667 15.8334H5M6.66667 17.5001H13.3333"
              stroke="#2D2D2D"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        name: 'Inventory 360',
        description: 'Real-time inventory tracking across channels',
        href: '#',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M2.725 5.80008L10 10.0084L17.275 5.80008M10 18.4001V10.0001M17.5 13.3334V6.66675C17.4997 6.37448 17.4225 6.08742 17.2763 5.83438C17.13 5.58134 16.9198 5.37122 16.6667 5.22508L10.8333 1.89175C10.58 1.74547 10.2926 1.66846 10 1.66846C9.70744 1.66846 9.42003 1.74547 9.16667 1.89175L3.33333 5.22508C3.08022 5.37122 2.86998 5.58134 2.72372 5.83438C2.57745 6.08742 2.5003 6.37448 2.5 6.66675V13.3334C2.5003 13.6257 2.57745 13.9127 2.72372 14.1658C2.86998 14.4188 3.08022 14.6289 3.33333 14.7751L9.16667 18.1084C9.42003 18.2547 9.70744 18.3317 10 18.3317C10.2926 18.3317 10.58 18.2547 10.8333 18.1084L16.6667 14.7751C16.9198 14.6289 17.13 14.4188 17.2763 14.1658C17.4225 13.9127 17.4997 13.6257 17.5 13.3334Z"
              stroke="#2D2D2D"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        name: 'Accounts 360',
        description: 'End-to-end financial operations and bookkeeping',
        href: '#',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M17.5 10.0001V5.83341C17.5 5.39139 17.3244 4.96746 17.0118 4.6549C16.6993 4.34234 16.2754 4.16675 15.8333 4.16675H4.16667C3.72464 4.16675 3.30072 4.34234 2.98816 4.6549C2.67559 4.96746 2.5 5.39139 2.5 5.83341V14.1667C2.5 14.6088 2.67559 15.0327 2.98816 15.3453C3.30072 15.6578 3.72464 15.8334 4.16667 15.8334H15.8333C16.2754 15.8334 16.6993 15.6578 17.0118 15.3453C17.3244 15.0327 17.5 14.6088 17.5 14.1667V10.0001ZM17.5 10.0001H13.3333M13.3333 10.0001C13.3333 9.55805 13.5089 9.13413 13.8215 8.82157C14.134 8.50901 14.558 8.33341 15 8.33341H17.5M13.3333 10.0001C13.3333 10.4421 13.5089 10.866 13.8215 11.1786C14.134 11.4912 14.558 11.6667 15 11.6667H17.5"
              stroke="#2D2D2D"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Education Suite',
    color: 'text-brand-red',
    products: [
      {
        name: 'Metaversity',
        description: 'Immersive XR learning environments for institutions',
        href: '#',
        badge: 'NEW',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M5.00033 10.0001V14.1667C5.00033 15.4167 8.33366 16.6667 10.0003 16.6667C11.667 16.6667 15.0003 15.4167 15.0003 14.1667V10.0001M18.3337 8.33341L10.0003 4.16675L1.66699 8.33341L10.0003 12.5001L18.3337 8.33341Z"
              stroke="#2D2D2D"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        name: 'HR Verse',
        description: 'AI-driven interviewing, hiring, and workforce learning',
        href: '#',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M13.3337 17.5V15.8333C13.3337 14.9493 12.9825 14.1014 12.3573 13.4763C11.7322 12.8512 10.8844 12.5 10.0003 12.5H5.00033C4.11627 12.5 3.26842 12.8512 2.6433 13.4763C2.01818 14.1014 1.66699 14.9493 1.66699 15.8333V17.5M18.3337 17.5V15.8333C18.3331 15.0948 18.0873 14.3773 17.6348 13.7936C17.1823 13.2099 16.5488 12.793 15.8337 12.6083M13.3337 2.60833C14.0507 2.79192 14.6862 3.20892 15.14 3.79359C15.5939 4.37827 15.8402 5.09736 15.8402 5.8375C15.8402 6.57764 15.5939 7.29673 15.14 7.88141C14.6862 8.46608 14.0507 8.88308 13.3337 9.06667M7.50033 9.16667C8.38438 9.16667 9.23223 8.81548 9.85735 8.19036C10.4825 7.56523 10.8337 6.71739 10.8337 5.83333C10.8337 4.94928 10.4825 4.10143 9.85735 3.47631C9.23223 2.85119 8.38438 2.5 7.50033 2.5C6.61627 2.5 5.76842 2.85119 5.1433 3.47631C4.51818 4.10143 4.16699 4.94928 4.16699 5.83333C4.16699 6.71739 4.51818 7.56523 5.1433 8.19036C5.76842 8.81548 6.61627 9.16667 7.50033 9.16667Z"
              stroke="#2D2D2D"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Developer Suite',
    color: 'text-brand-red',
    products: [
      {
        name: 'Server Health',
        description: 'Continuous infrastructure monitoring and alerting',
        href: '#',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <g clipPath="url(#clip0_1226_2302)">
              <path
                d="M5.83333 4.99992H5.84167M5.83333 11.6666H5.84167M5.83333 18.3333H5.84167M2.5 3.33325H17.5V6.66659H2.5V3.33325ZM2.5 9.99992H17.5V13.3333H2.5V9.99992ZM2.5 16.6666H17.5V19.9999H2.5V16.6666Z"
                stroke="#2D2D2D"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_1226_2302">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        ),
      },
      {
        name: 'Assets Manager',
        description: 'Centralized digital asset management for engineering',
        href: '#',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M3.33366 3.33325H8.33366L10.0003 4.99992H16.667C17.109 4.99992 17.5329 5.17551 17.8455 5.48807C18.1581 5.80063 18.3337 6.22456 18.3337 6.66659V14.9999C18.3337 15.4419 18.1581 15.8659 17.8455 16.1784C17.5329 16.491 17.109 16.6666 16.667 16.6666H3.33366C2.89163 16.6666 2.46771 16.491 2.15515 16.1784C1.84259 15.8659 1.66699 15.4419 1.66699 14.9999V4.99992C1.66699 4.55789 1.84259 4.13397 2.15515 3.82141C2.46771 3.50885 2.89163 3.33325 3.33366 3.33325ZM7.50033 10.8333H9.16699V12.4999H7.50033V10.8333ZM10.8337 10.8333H12.5003V12.4999H10.8337V10.8333ZM7.50033 14.1666H9.16699V15.8333H7.50033V14.1666ZM10.8337 14.1666H12.5003V15.8333H10.8337V14.1666Z"
              stroke="#2D2D2D"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        name: 'Agent 360',
        description: 'Build, deploy, and manage AI agents at scale',
        href: '#',
        badge: 'NEW',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <g clipPath="url(#clip0_1226_2321)">
              <path
                d="M10 2.5L11.2875 6.3625L15 7.5L11.2875 8.6375L10 12.5L8.7125 8.6375L5 7.5L8.7125 6.3625L10 2.5ZM15.8333 11.6667L16.6667 14.1667L19.1667 15L16.6667 15.8333L15.8333 18.3333L15 15.8333L12.5 15L15 14.1667L15.8333 11.6667Z"
                stroke="#2D2D2D"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_1226_2321">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        ),
      },
    ],
  },
]

const exploreLinks = [
  { label: 'All Codefied Suites', href: '#' },
  { label: 'Compare suites', href: '#' },
  { label: 'Talk to sales', href: '#' },
]

const SUITE_ICON_BG: Record<string, string> = {
  'Business Suite':
    'bg-[linear-gradient(135deg,#FFD9D0_0%,#FFDBD2_16.67%,#FFDDD4_33.33%,#FFDFD6_50%,#FFE1D8_66.67%,#FFE3DA_83.33%,#FFE5DC_100%)]',
  'Education Suite':
    'bg-[linear-gradient(135deg,#DDF1FF_0%,#E0F2FF_25%,#E3F3FF_50%,#E5F4FF_75%,#E8F5FF_100%)]',
  'Developer Suite':
    'bg-[linear-gradient(135deg,#D4F1D8_0%,#D6F2DA_20%,#D9F3DC_40%,#DBF3DF_60%,#DEF4E1_80%,#E0F5E3_100%)]',
}

function ProductLink({
  suiteLabel,
  product,
  onNavigate,
  className,
}: {
  suiteLabel: string
  product: Product
  onNavigate?: () => void
  className?: string
}) {
  return (
    <Link
      to={product.href}
      onClick={onNavigate}
      className={cn(
        'group flex items-start gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-brand-gray-50',
        className,
      )}
    >
      <span
        className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] px-[10px] py-0 text-brand-gray-500 ${SUITE_ICON_BG[suiteLabel]}`}
      >
        {product.icon}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="flex flex-wrap items-center gap-1.5 font-sans text-[16px] font-medium not-italic leading-[24px] text-[#2D2D2D]">
          {product.name}
          {product.badge && (
            <span
              className="flex items-center justify-center rounded-full bg-[#E8E0FF] pt-[6.422px] pr-[8.75px] pb-[6.578px] pl-[8.25px] font-sans text-[11px] font-medium not-italic uppercase leading-[16.5px] tracking-[0.55px] text-[#6B4DE8]"
              style={
                {
                  leadingTrim: 'both',
                  textEdge: 'cap',
                } as CSSProperties
              }
            >
              {product.badge}
            </span>
          )}
        </span>
        <span className="mt-0.5 font-sans text-[12px] font-normal not-italic leading-[18.2px] text-[#979AC0]">
          {product.description}
        </span>
      </span>
    </Link>
  )
}

function ExploreSection({
  layout,
  onNavigate,
}: {
  layout: 'desktop' | 'mobile'
  onNavigate?: () => void
}) {
  return (
    <div
      className={cn(
        'w-full shrink-0',
        layout === 'desktop'
          ? 'border-t border-brand-gray-100 pt-[33px] [@media(max-height:900px)]:pt-6 [@media(max-height:780px)]:pt-5 [@media(max-height:680px)]:pt-4'
          : 'pt-1',
      )}
    >
      <p
        className={cn(
          layout === 'desktop'
            ? 'mb-2 font-sans text-[14px] font-bold not-italic uppercase leading-[21px] tracking-[0.84px] text-[#D4575B]'
            : 'mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-brand-gray-400',
        )}
      >
        Explore
      </p>
      <div
        className={cn(
          layout === 'desktop'
            ? 'mt-4 flex items-center gap-5 [@media(max-height:780px)]:mt-3 [@media(max-height:780px)]:gap-4 [@media(max-height:680px)]:mt-2 [@media(max-height:680px)]:gap-3'
            : 'flex flex-col',
        )}
      >
        {exploreLinks.map((link) => (
          <Link
            key={link.label}
            to={link.href}
            onClick={onNavigate}
            className={cn(
              'transition-colors hover:opacity-70',
              layout === 'desktop'
                ? 'flex items-center gap-0.5 font-sans text-[14px] font-normal not-italic leading-[21px] text-[#2D2D2D]'
                : 'rounded-md px-2 py-2 text-sm text-brand-gray-600 hover:text-brand-black',
            )}
          >
            {link.label}
            {layout === 'desktop' && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M4.5 9L7.5 6L4.5 3"
                  stroke="#2D2D2D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </Link>
        ))}
      </div>
    </div>
  )
}

export function MegaMenuMobile({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="mt-1 flex flex-col gap-4 pl-4">
      {suites.map((suite) => (
        <div key={suite.label}>
          <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-brand-gray-400">
            {suite.label}
          </p>
          <div className="flex flex-col">
            {suite.products.map((product) => (
              <Link
                key={product.name}
                to={product.href}
                onClick={onNavigate}
                className="rounded-md px-2 py-2 text-sm text-brand-gray-600 transition-colors hover:text-brand-black"
              >
                {product.name}
              </Link>
            ))}
          </div>
        </div>
      ))}
      <ExploreSection layout="mobile" onNavigate={onNavigate} />
    </div>
  )
}

interface ProjectItem {
  name: string
  description: string
  href: string
  logo: string
  preview: string
  iconBg: string
}

const projects: ProjectItem[] = [
  {
    name: 'VOCA',
    description: 'AI voice agents for modern business automation.',
    href: 'https://voca.codefied.co/',
    logo: vocaLogoUrl,
    preview: vocaPreviewUrl,
    iconBg: 'bg-[#FF7B00]/13',
  },
  {
    name: 'Sprinto',
    description: 'Your AI-powered business operating system.',
    href: 'https://sprinto-landing-steel.vercel.app/',
    logo: sprintoLogoUrl,
    preview: sprintoPreviewUrl,
    iconBg: 'bg-[linear-gradient(135deg,#DDF1FF_0%,#E5F5FF_50%,#EDF8FF_100%)]',
  },
  {
    name: 'Dispatchly',
    description: 'Smart freight dispatching and logistics operations.',
    href: 'https://dispatchly-landing.vercel.app/',
    logo: dispatchlyLogoUrl,
    preview: dispatchlyPreviewUrl,
    iconBg: 'bg-[linear-gradient(135deg,#FFD9D0_0%,#FFE1D8_50%,#FFE9E2_100%)]',
  },
]

export function ProjectsMenuMobile({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="mt-1 flex flex-col pl-4">
      {projects.map((project) => (
        <a
          key={project.name}
          href={project.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
          className="rounded-md px-2 py-2 text-sm text-brand-gray-600 transition-colors hover:text-brand-black"
        >
          {project.name}
        </a>
      ))}
    </div>
  )
}

export function ProjectsMenu() {
  const [activeIdx, setActiveIdx] = useState(0)
  const active = projects[activeIdx]

  return (
    <div className="w-full max-w-[772px] flex gap-6 p-5 overflow-hidden rounded-b-[20px] border-t border-black/5 bg-white shadow-[0_8px_32px_0_rgba(0,0,0,0.08)]">
      <div className="flex w-full max-w-[348px] h-[219px] shrink-0 flex-col justify-between">
        {projects.map((project, i) => (
          <a
            key={project.name}
            href={project.href}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => setActiveIdx(i)}
            className={cn(
              'group flex items-start gap-3 rounded-xl px-2 py-2.5 transition-colors',
              activeIdx === i ? 'bg-brand-gray-50' : 'hover:bg-brand-gray-50',
            )}
          >
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] ${project.iconBg}`}
            >
              <img src={project.logo} alt="" className="h-4 w-auto" />
            </span>
            <span className="flex flex-col">
              <span className="font-sans text-[16px] font-medium leading-[22px] text-[#2D2D2D]">
                {project.name}
              </span>
              <span className="mt-0.5 font-sans text-[13px] font-normal leading-[18px] text-[#979AC0]">
                {project.description}
              </span>
            </span>
          </a>
        ))}
      </div>
      <div className="relative flex-1 overflow-hidden w-[360px] h-[219px]">
        <AnimatePresence>
          <motion.img
            key={active.name}
            src={active.preview}
            alt={active.name}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
      </div>
    </div>
  )
}

const companyLinks = [
  {
    name: 'About Us',
    description: 'The team, thinking, and story behind Codefied',
    href: '/about',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M14.1663 17.5V15.8333C14.1663 14.9493 13.8152 14.1014 13.19 13.4763C12.5649 12.8512 11.7171 12.5 10.833 12.5H4.16634C3.28229 12.5 2.43444 12.8512 1.80932 13.4763C1.1842 14.1014 0.833008 14.9493 0.833008 15.8333V17.5M19.1663 17.5V15.8333C19.1658 15.0948 18.92 14.3773 18.4675 13.7936C18.015 13.2099 17.3814 12.793 16.6663 12.6083M13.333 2.60833C14.05 2.79192 14.6855 3.20892 15.1394 3.79359C15.5932 4.37827 15.8395 5.09736 15.8395 5.8375C15.8395 6.57764 15.5932 7.29673 15.1394 7.88141C14.6855 8.46608 14.05 8.88308 13.333 9.06667M7.49967 9.16667C8.38373 9.16667 9.23158 8.81548 9.8567 8.19036C10.4818 7.56523 10.833 6.71739 10.833 5.83333C10.833 4.94928 10.4818 4.10143 9.8567 3.47631C9.23158 2.85119 8.38373 2.5 7.49967 2.5C6.61562 2.5 5.76777 2.85119 5.14265 3.47631C4.51753 4.10143 4.16634 4.94928 4.16634 5.83333C4.16634 6.71739 4.51753 7.56523 5.14265 8.19036C5.76777 8.81548 6.61562 9.16667 7.49967 9.16667Z"
          stroke="#2D2D2D"
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    iconBg: 'bg-[linear-gradient(135deg,#FFE4E1_0%,#FFEAE7_50%,#FFF0ED_100%)]',
  },
  {
    name: 'Blogs',
    description: 'Field notes on engineering, product, and the work',
    href: '/blog',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M16.6663 6.66675L11.6663 1.66675H4.99967C4.55765 1.66675 4.13372 1.84234 3.82116 2.1549C3.5086 2.46746 3.33301 2.89139 3.33301 3.33341V16.6667C3.33301 17.1088 3.5086 17.5327 3.82116 17.8453C4.13372 18.1578 4.55765 18.3334 4.99967 18.3334H14.9997C15.4417 18.3334 15.8656 18.1578 16.1782 17.8453C16.4907 17.5327 16.6663 17.1088 16.6663 16.6667V6.66675ZM11.6663 1.66675V6.66675H16.6663M13.333 10.8334H6.66634M13.333 14.1667H6.66634M8.33301 7.50008H6.66634"
          stroke="#2D2D2D"
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    iconBg: 'bg-[linear-gradient(135deg,#DDF1FF_0%,#E5F5FF_50%,#EDF8FF_100%)]',
  },
  {
    name: 'Careers',
    description: "Join the team building Codefied's next chapter",
    href: '/careers',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M16.667 5.83333H13.3337V4.16667C13.3337 3.72464 13.1581 3.30072 12.8455 2.98816C12.5329 2.67559 12.109 2.5 11.667 2.5H8.33366C7.89163 2.5 7.46771 2.67559 7.15515 2.98816C6.84259 3.30072 6.66699 3.72464 6.66699 4.16667V5.83333H3.33366C2.89163 5.83333 2.46771 6.00893 2.15515 6.32149C1.84259 6.63405 1.66699 7.05797 1.66699 7.5V15.8333C1.66699 16.2754 1.84259 16.6993 2.15515 17.0118C2.46771 17.3244 2.89163 17.5 3.33366 17.5H16.667C17.109 17.5 17.5329 17.3244 17.8455 17.0118C18.1581 16.6993 18.3337 16.2754 18.3337 15.8333V7.5C18.3337 7.05797 18.1581 6.63405 17.8455 6.32149C17.5329 6.00893 17.109 5.83333 16.667 5.83333ZM8.33366 4.16667H11.667V5.83333H8.33366V4.16667Z"
          stroke="#2D2D2D"
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    iconBg: 'bg-[linear-gradient(135deg,#D4F1D8_0%,#DCF4DF_50%,#E4F6E7_100%)]',
  },
]

export function CompanyMenuMobile({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="mt-1 flex flex-col pl-4">
      {companyLinks.map((link) => (
        <Link
          key={link.name}
          to={link.href}
          onClick={onNavigate}
          className="rounded-md px-2 py-2 text-sm text-brand-gray-600 transition-colors hover:text-brand-black"
        >
          {link.name}
        </Link>
      ))}
    </div>
  )
}

export function CompanyMenu() {
  return (
    <div
      style={{ width: 400, height: 259 }}
      className="flex flex-col items-start rounded-b-[20px] border-t border-black/5 bg-white px-5 pt-5 pb-0 shadow-[0_8px_32px_0_rgba(0,0,0,0.08)]"
    >
      {companyLinks.map((link) => (
        <Link
          key={link.name}
          to={link.href}
          className="group flex w-full items-center gap-3 rounded-xl px-2 py-3 transition-colors hover:bg-brand-gray-50"
        >
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] ${link.iconBg}`}
          >
            {link.icon}
          </span>
          <span className="flex flex-col">
            <span className="font-sans text-[15px] font-medium leading-[22px] text-[#2D2D2D]">
              {link.name}
            </span>
            <span className="font-sans text-[12px] font-normal leading-[18px] text-[#979AC0]">
              {link.description}
            </span>
          </span>
        </Link>
      ))}
    </div>
  )
}

export function MegaMenu() {
  return (
    <div className="scrollbar-hide box-border flex h-full min-h-0 w-full shrink-0 flex-col items-start gap-[47.875px] overflow-x-hidden overflow-y-auto rounded-2xl border border-brand-gray-100 bg-white px-[100px] pt-[48px] pb-6 shadow-[0_8px_40px_rgba(0,0,0,0.12)] [@media(max-height:900px)]:gap-10 [@media(max-height:900px)]:px-20 [@media(max-height:900px)]:pt-10 [@media(max-height:780px)]:gap-7 [@media(max-height:780px)]:px-16 [@media(max-height:780px)]:pt-8 [@media(max-height:780px)]:pb-4 [@media(max-height:680px)]:gap-5 [@media(max-height:680px)]:px-12 [@media(max-height:680px)]:pt-6 [@media(max-height:680px)]:pb-3">
      {/* Suites grid */}
      <div className="grid w-full grid-cols-3 gap-0">
        {suites.map((suite) => (
          <div key={suite.label} className="pr-12 last:pr-0">
            <p className="mb-3 font-sans text-[14px] font-bold not-italic uppercase leading-[21px] tracking-[0.84px] text-[#D4575B] [@media(max-height:680px)]:mb-2">
              {suite.label}
            </p>
            <div className="mt-6 flex flex-col gap-1 [@media(max-height:900px)]:mt-5 [@media(max-height:780px)]:mt-3 [@media(max-height:680px)]:mt-2">
              {suite.products.map((product) => (
                <ProductLink
                  key={product.name}
                  suiteLabel={suite.label}
                  product={product}
                  className="[@media(max-height:780px)]:py-2 [@media(max-height:680px)]:gap-2 [@media(max-height:680px)]:py-1.5"
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <ExploreSection layout="desktop" />
    </div>
  )
}
